import {
  ShippingMethodCode,
  ShippingMethodsByIdQuery,
  ShippingMethodMethodType,
  CustomerAddressType,
  UpdateCustomerAddressMutationVariables,
  ReviewType,
} from "../../../../generated/graphql";
import { useMarketExpressDataState } from "../../../../providers/marketMode";
import {
  Address,
  AddressSuggestion,
  Category,
  Coupon,
  CreateOrderData,
  OrderReviewType,
  PaymentMethodIdEnum,
  Product,
  ProductImageTypes,
  ProductReview,
  ProductType,
  ReviewSet,
  ShippingMethod,
  ShippingMethodDeliveryType,
  ShippingMethodType,
  StoryType,
  TOrder,
  User,
  CartState,
  RawCartResponse,
  CreateYooKassaPaymentInput,
  CreateYooKassaPaymentEF,
  ShippingLine,
  OrderPaymentStatus,
  SupaAdditionalFeature,
  AdditionalFeature,
} from "@core/types";
import {
  CreateParhatoOrder,
  Customer,
  OrderReviewSB,
  ParhatoAddressObject,
  ParhatoCategory,
  ParhatoCoupon,
  ParhatoOrderWithPaymentStatus,
  ParhatoProduct,
  ParhatoProductReview,
  SupaOrderDataType,
  SupaStory,
} from "./types";
import { getPlatforms } from "@ionic/react";
import { getCustomerFullAddress } from "../../../../utils";
import { removeHtmlEntities } from "./utils";

const codeBaseVersion = process.env.REACT_APP_PARHATO_CODE_VERSION!;

export const convertSupaOrderDataToBaseOrder = (orderData: SupaOrderDataType): TOrder => {
  const metaDataObject = {} as any;

  orderData?.meta_data?.forEach((meta_data: any) => {
    metaDataObject[meta_data.key] = meta_data.value;
  });

  const shippingLineOrder = orderData.shipping_lines?.[0];

  const shippingLine: ShippingLine = {
    methodTitle: shippingLineOrder?.method_title,
    methodId: shippingLineOrder?.method_id,
    total: shippingLineOrder?.total,
    shipId: shippingLineOrder?.id,
  };

  const lineItems = orderData.line_items?.map((lineItem) => ({
    image: lineItem.image,
    name: lineItem.name,
    price: lineItem.price,
    total: lineItem.total,
    id: String(lineItem.id),
    subtotal: lineItem.subtotal,
    quantity: lineItem.quantity,
    productId: String(lineItem.product_id),
    variationId: String(lineItem.variation_id),
  })) as TOrder["lineItems"];

  const paymentStatus =
    orderData.payment_status === "pending" || orderData.needs_payment
      ? OrderPaymentStatus.WAITING_PAYMENT
      : orderData.payment_status === "succeeded"
        ? OrderPaymentStatus.PAYED
        : OrderPaymentStatus.FAILED;

  return {
    needsPayment: orderData.needs_payment,
    id: orderData.id,
    dateCreated: orderData.created_at,
    number: orderData.id.toString(),
    currencySymbol: orderData.currency_symbol,
    total: orderData.cart_total,
    discountTotal: orderData.discount_total,
    shippingTotal: orderData.shipping_total,
    customerNote: orderData.customer_note,
    status: orderData.status,

    deliveryTime: metaDataObject["pi_delivery_time"],
    deliveryDate: metaDataObject["pi_delivery_date"],

    paymentStatus: paymentStatus,
    shippingLine: shippingLine,
    lineItems: lineItems,

    paymentMethod: {
      id: orderData.payment_method,
      title: orderData.payment_method_title,
      type: orderData.payment_method === "bacs" ? PaymentMethodIdEnum.card : PaymentMethodIdEnum.cash,
    },

    customer: {
      email: "",
      id: String(orderData.customer_id),
      phone: orderData?.customer_phone,
      lastName: orderData?.customer_lastname,
      firstName: orderData?.customer_firstname,
      whatsappNumber: metaDataObject["_order_whatsapp"],
    },

    address: {
      city: orderData?.city,
      house: orderData?.house,
      street: orderData?.street,
      latitude: String(orderData.latitude),
      longitude: String(orderData.longitude),
      intercom: metaDataObject["domofon"],
      floor: metaDataObject["billing_address_5"],
      isFlat: metaDataObject["kv"] === "Квартира",
      entrance: metaDataObject["billing_address_4"],
      fullAddress: metaDataObject["search_address"],
      apartmentNumber: metaDataObject["billing_address_3"],
    },
  };
};

/**
 * @param order take a parhato order and convert to base order
 */
export function convertParhatoOrderToBaseOrder(order: ParhatoOrderWithPaymentStatus): TOrder {
  const metaDataObject: any = {};

  order?.meta_data?.forEach((meta_data) => {
    metaDataObject[meta_data.key] = meta_data.value;
  });

  return {
    id: order.id,
    dateCreated: order.date_created,
    number: order.number,
    lineItems: order.line_items.map((item) => ({
      ...item,
      id: String(item.id),
      productId: String(item.product_id),
      variationId: String(item.variation_id),
      price: String(item.price),
      image: { src: item.image.src, type: ProductImageTypes.img },
    })),
    shippingLine: order.shipping_lines[0]
      ? {
          shipId: String(order.shipping_lines[0].id),
          methodId: order.shipping_lines[0].method_id! as ShippingMethodCode,
          methodTitle: order.shipping_lines[0].method_title,
          total: String(order.shipping_lines[0].total),
        }
      : undefined,
    currencySymbol: order.currency_symbol,
    total: parseFloat(order.total),
    discountTotal: order.discount_total,
    shippingTotal: order.shipping_total,
    customerNote: order.customer_note,
    status: order.status,
    paymentStatus: order.payment_status,
    paymentMethod: {
      id: order.payment_method,
      title: order.payment_method_title,
      type: order.payment_method === "bacs" ? PaymentMethodIdEnum.card : PaymentMethodIdEnum.cash,
    },
    deliveryTime: metaDataObject["pi_delivery_time"],
    deliveryDate: metaDataObject["pi_delivery_date"],
    customer: {
      id: String(order.customer_id),
      email: order.billing.email,
      phone: order.billing.phone,
      lastName: order.billing.last_name,
      firstName: order.billing.first_name,
      whatsappNumber: metaDataObject["_order_whatsapp"],
    },
    address: {
      city: order.shipping.city,
      house: order.shipping.address_2,
      street: order.shipping.address_1,
      // postcode: order.billing.postcode,
      latitude: metaDataObject["latitude"],
      longitude: metaDataObject["longitude"],
      intercom: metaDataObject["domofon"],
      floor: metaDataObject["billing_address_5"],
      isFlat: metaDataObject["kv"] === "Квартира",
      entrance: metaDataObject["billing_address_4"],
      fullAddress: metaDataObject["search_address"],
      apartmentNumber: metaDataObject["billing_address_3"],
    },
  };
}

/**
 * Change the created order to the parhato type
 * @param order take a base order without id and convert to parhato order
 */
export function convertCreateOrderToParhato(order: CreateOrderData): Partial<CreateParhatoOrder> {
  const metaData = [
    {
      key: "_order_whatsapp",
      value: order.customer?.whatsappNumber,
    },
    {
      key: "app_info",
      value: `from parhato app v${codeBaseVersion}; ${getPlatforms()}`,
    },
    {
      key: "kv",
      value: order.address?.isFlat ? "Квартира" : "Частный дом",
    },
    {
      key: "domofon",
      value: order.address?.intercom || "",
    },
    {
      key: "billing_address_3",
      value: order.address?.apartmentNumber || "",
    },
    {
      key: "billing_address_4",
      value: order?.address?.entrance || "",
    },
    {
      key: "search_address",
      value: order.address?.fullAddress || "",
    },
    {
      key: "billing_address_5",
      value: order.address?.floor || "",
    },
    {
      key: "longitude",
      value: order.address?.longitude || "",
    },
    {
      key: "latitude",
      value: order.address?.latitude || "",
    },
  ];

  if (order.deliveryDate) {
    metaData.push(
      {
        key: "pi_system_delivery_date",
        value:
          order.deliveryDate!.getFullYear() +
          "/" +
          String(order.deliveryDate!.getMonth() + 1).padStart(2, "0") +
          "/" +
          String(order.deliveryDate!.getDate()).padStart(2, "0"),
      },
      {
        key: "pi_delivery_date",
        value:
          String(order.deliveryDate!.getDate()).padStart(2, "0") +
          "." +
          String(order.deliveryDate!.getMonth() + 1).padStart(2, "0") +
          "." +
          order.deliveryDate!.getFullYear(),
      },
      {
        key: "pi_delivery_type",
        value: "delivery",
      },
      {
        key: "pi_delivery_time",
        value: order.deliveryTime,
      },
    );
  }

  if (useMarketExpressDataState.getState().mode === "express") {
    metaData.push({
      key: "parhato_delivery_type",
      value: "express",
    });
  }

  return {
    customer_id: Number(order.customer?.id!),

    line_items: order.lineItems?.map((item) => {
      return {
        price: item.price,
        quantity: item.quantity,
        product_id: Number(item.id),
        variation_id: item.variationId ? Number(item.variationId) : 0,
        total: item.total,
      };
    }),

    status: order.status,
    customer_note: order.customerNote,
    shipping_lines: [
      {
        method_id: order.shippingLine.methodId!,
        method_title: order.shippingLine.methodTitle!,
        total: order.shippingLine.total ?? "0.00",
      },
    ],
    payment_method: order.paymentMethod.id,
    payment_method_title: order.paymentMethod.title,

    meta_data: metaData,

    billing: {
      notice: "",
      company: "",
      country: "",
      billing_country: "",

      phone: order?.customer?.phone ?? "",
      email: order?.customer?.email ?? "",

      last_name: order?.customer?.lastName ?? "",
      first_name: order?.customer?.firstName ?? "",

      billing_city: order?.address?.city ?? "",
      billing_address_2: order?.address?.house ?? "",
      billing_address_1: order?.address?.street ?? "",
      billing_address_5: order?.address?.floor ?? "",
      billing_address_4: order?.address?.entrance ?? "",
      billing_address_3: order?.address?.apartmentNumber ?? "",

      address_1: order?.address?.street ?? "",
      address_2: order?.address?.house ?? "",

      city: order?.address?.city ?? "",
      kv: order?.address?.apartmentNumber ?? "",
      postcode: "",
      domofon: order?.address?.intercom ?? "",

      state: order?.address?.city ?? "",
      latitude: order?.address?.latitude ?? "",
      longitude: order?.address?.longitude ?? "",
      search_address: order?.address?.fullAddress ?? "",
    },
    shipping: {
      company: "",
      country: "",

      address_1: order?.address?.street ?? "",
      address_2: order?.address?.house ?? "",

      city: order?.address?.city ?? "",

      shipping_phone: order?.customer?.whatsappNumber ?? "",
      first_name: order?.customer?.firstName ?? "",
      last_name: order?.customer?.lastName ?? "",
      phone: order?.customer?.phone ?? "",

      postcode: "",
      state: order?.address?.city ?? "",
    },
    coupon_lines: order.coupons?.map(({ code }) => ({ code })),
    reward_points: order.rewardPoints,
    cart_total: order.total,
  };
}

export function convertShippingMethod(
  shippingMethod: ShippingMethodsByIdQuery["customerShippingMethodsByZoneId"]["edges"][number]["node"],
): ShippingMethod {
  const shippingMethodDeliveryTypeMap: Record<ShippingMethodMethodType, ShippingMethodDeliveryType> = {
    [ShippingMethodMethodType.Default]: ShippingMethodDeliveryType.default,
    [ShippingMethodMethodType.NextDay]: ShippingMethodDeliveryType.nextDay,
  };

  const shippingMethodTypeMap: Record<ShippingMethodCode, ShippingMethodType> = {
    [ShippingMethodCode.CdekCourier]: ShippingMethodType.courier,
    [ShippingMethodCode.CdekPoint]: ShippingMethodType.pickupPoint,
    [ShippingMethodCode.FlatRate]: ShippingMethodType.flatRate,
    [ShippingMethodCode.FreeShipping]: ShippingMethodType.freeShipping,
    [ShippingMethodCode.PickupPoint]: ShippingMethodType.pickupPoint,
    [ShippingMethodCode.TaxiShipping]: ShippingMethodType.taxiShipping,
  };

  return {
    id: shippingMethod.id,
    description: shippingMethod.description,
    enabled: shippingMethod.isEnabled,
    methodDeliveryType: shippingMethodDeliveryTypeMap[shippingMethod.methodType],
    methodId: shippingMethod.code,
    methodType: shippingMethodTypeMap[shippingMethod.code],
    title: shippingMethod.title,
    cost: shippingMethod.cost,
    minAmount: shippingMethod.minAmount,
    minDeliveryDays: shippingMethod.minDeliveryDays,
    maxDeliveryDays: shippingMethod.maxDeliveryDays,
  };
}

export function convertParhatoUserToUser(user: Customer): User {
  return {
    id: String(user.id),
    firstName: user.first_name,
    lastName: user.last_name,
    phone: user.billing.phone,
    email: user.email,
    userName: user.username,
    token: "",
  };
}

export function convertParhatoCoupon(coupon: ParhatoCoupon): Coupon {
  return {
    id: String(coupon.id),
    code: coupon.code,
    discountAmount: parseFloat(coupon.amount),
    discountType: coupon.discount_type,
    minimumAmount: parseFloat(coupon.minimum_amount),
    maximumAmount: parseFloat(coupon.maximum_amount),
    dateExpires: coupon.date_expires,
    individualUse: coupon.individual_use,
    freeShipping: coupon.free_shipping,
    excludeSaleItems: coupon.exclude_sale_items,
    productIds: coupon.product_ids,
    excludedProductIds: coupon.excluded_product_ids,
    productCategories: coupon.product_categories,
    excludedProductCategories: coupon.excluded_product_categories,
  };
}

const portionUnits = {
  KGM: "кг",
  PCE: "шт",
};

export const convertParhatoProduct = (parhatoProduct: ParhatoProduct): Product => {
  const findPortionUnitAcronym: keyof typeof portionUnits = parhatoProduct?.meta_data?.find(
    (meta) => meta.key === "_unit",
  )?.value?.internationalAcronym;

  const portionUnit = portionUnits[findPortionUnitAcronym];

  return {
    id: String(parhatoProduct.id),
    name: parhatoProduct.name,
    price: parhatoProduct.price,
    onSale: parhatoProduct.on_sale,
    stockStatus: parhatoProduct.stock_status,
    description: parhatoProduct.description,
    attributes: parhatoProduct.attributes?.map((attr) => ({
      ...attr,
      id: String(attr.id),
    })),
    categories: parhatoProduct.categories?.map((category) => ({
      ...category,
      id: String(category.id),
    })),
    stockQuantity: parhatoProduct.stock_quantity,
    type: parhatoProduct.type as ProductType,
    purchasable: parhatoProduct.purchasable,
    images: parhatoProduct.images.map((img) => ({
      src: img.src,
      type: ProductImageTypes.img,
    })),
    soldIndividually: parhatoProduct.sold_individually,
    groupedProducts: parhatoProduct.grouped_products?.map((id) => String(id)),
    priceHtml: parhatoProduct.price_html,
    variations: parhatoProduct.variations?.map((id) => String(id)),
    averageRating: parhatoProduct.average_rating,
    dateCreated: parhatoProduct.date_created,
    defaultAttributes: parhatoProduct.default_attributes,
    parentId: String(parhatoProduct.parent_id),
    discountPrice: null,
    weight: parhatoProduct.weight || (portionUnit === portionUnits.KGM ? "1" : "0"),
    regularPrice: parhatoProduct.regular_price,
    portionUnit,
  };
};

export const convertParhatoCategory = (parhatoCategory: ParhatoCategory): Category => {
  return {
    id: String(parhatoCategory.id),
    name: removeHtmlEntities(parhatoCategory.name),
    description: parhatoCategory.description,
    image: parhatoCategory.image
      ? {
          src: parhatoCategory.image.src,
          id: parhatoCategory.image.id,
          name: parhatoCategory.image.name,
          alt: parhatoCategory.image.alt,
        }
      : undefined,
    parent: String(parhatoCategory.parent),
  };
};

export const convertParhatoReview = (review: ParhatoProductReview): ProductReview => {
  return {
    id: String(review.id),
    review: review.review,
    dateCreated: review.date_created,
    fullName: review.reviewer,
    rating: review.rating,
  };
};

export const convertAddressSuggestion = (address: ParhatoAddressObject): AddressSuggestion => {
  return {
    area: address.area,
    country: address.country,
    province: address.province,
    locality: address.locality,
    text: address.text,
    street: address.street,
    house: address.house,
    point: address.point.split(" ").reverse().join(" "),
    location: address.point.split(" ").reverse().map(Number),
    postcode: "",
    district: "",
  };
};

export const convertCustomerAddress = (address: CustomerAddressType): Address => {
  const fullAddress = getCustomerFullAddress(address);
  const [latitude, longitude] = address.location?.coordinates ?? [undefined, undefined];

  return {
    fullAddress,
    apartmentNumber: address.apartmentNumber,
    city: address.city,
    entrance: address.entrance,
    floor: address.floor,
    house: address.house,
    id: address.id,
    intercom: address.intercom,
    isFlat: address.isFlat,
    latitude: latitude,
    longitude: longitude,
    state: address.state,
    street: address.street,
    name: "",
    expressPointId: "",
  };
};

export const convertCustomerUpdateAddressPayload = (
  customerAddress: Address,
): UpdateCustomerAddressMutationVariables["address"] => {
  return {
    apartmentNumber: customerAddress.apartmentNumber!,
    city: customerAddress.city!,
    entrance: customerAddress.entrance!,
    floor: customerAddress.floor!,
    house: customerAddress.house!,
    intercom: customerAddress.intercom || "",
    isFlat: customerAddress.isFlat ?? false,
    state: customerAddress.state!,
    street: customerAddress.street!,
    latitude: customerAddress.latitude,
    longitude: customerAddress.longitude,
  };
};

export const convertOrderReview = (orderReview: ReviewType): OrderReviewType => {
  return {
    orderId: orderReview.order.id,
    assemblyQuality: orderReview.assemblyQuality,
    deliverySpeed: orderReview.deliverySpeed,
    driverCourtesy: orderReview.driverCourtesy,
    productQuality: orderReview.productQuality,
    customerNote: orderReview.customerNote,
  };
};

export const convertReviewSetToOrderReview = (reviewSetData: ReviewSet): OrderReviewType => {
  return {
    assemblyQuality: reviewSetData.reviewSet.assemblyQuality,
    deliverySpeed: reviewSetData.reviewSet.deliverySpeed,
    driverCourtesy: reviewSetData.reviewSet.driverCourtesy,
    orderId: String(reviewSetData.orderNumber),
    productQuality: reviewSetData.reviewSet.productQuality,
    customerNote: reviewSetData.reviewSet.customerNote,
  };
};

export const convertOrderReviewSB = (review: OrderReviewType): Omit<OrderReviewSB, "id" | "created_at"> => {
  return {
    delivery_speed: review.deliverySpeed!,
    driver_courtesy: review.driverCourtesy!,
    product_quality: review.productQuality,
    assembly_quality: review.assemblyQuality!,
    customer_note: review.customerNote ?? "",
    order_id: Number(review.orderId),
  };
};

export const convertSBOrderReview = (review: OrderReviewSB): OrderReviewType => {
  return {
    deliverySpeed: review.delivery_speed,
    driverCourtesy: review.driver_courtesy,
    productQuality: review.product_quality,
    assemblyQuality: review.assembly_quality,
    customerNote: review.customer_note ?? "",
    orderId: String(review.order_id),
  };
};

export const convertStoriesData = (story: SupaStory): StoryType => {
  return {
    id: story.id,
    name: story.name,
    thumbnailURL: story.thumbnail_url,
    content: story.stories_content.map((slide) => ({
      id: slide.id,
      src: slide.src,
      type: slide.type,
      button: slide.button_role
        ? {
            role: slide.button_role === "route" ? "route" : "_blank",
            text: slide.button_text,
            link: slide.button_link,
          }
        : undefined,
    })),
  };
};

export const convertSupaCartToCart = (cartFromSupa: RawCartResponse): CartState => {
  return {
    cartTotalValue: cartFromSupa.total,
    cartSubtotalValue: cartFromSupa.total + cartFromSupa.reward_points,
    cartProducts: cartFromSupa.products,
    cartProductsMap: cartFromSupa.products.reduce(
      (acc, product) => {
        acc[product.id] = product;
        return acc;
      },
      {} as CartState["cartProductsMap"],
    ),
    coupons: cartFromSupa.coupons,
    rewardPoints: cartFromSupa.reward_points,
    soldOutProducts: cartFromSupa.sold_out_products,
    unavailableProducts: cartFromSupa.unavailable_products,
    updatedAt: cartFromSupa.updated_at,
  };
};

export const convertCartToSupaCart = (cart: CartState): RawCartResponse => {
  return {
    total: cart.cartSubtotalValue,
    products: cart.cartProducts,
    coupons: cart.coupons,
    reward_points: cart.rewardPoints,
    sold_out_products: cart.soldOutProducts,
    unavailable_products: cart.unavailableProducts,
    updated_at: cart.updatedAt,
  };
};

export const convertYooKassaPayloadForSupa = (
  createYooKassaPaymentInput: CreateYooKassaPaymentInput,
): CreateYooKassaPaymentEF => {
  return {
    amount: {
      value: createYooKassaPaymentInput.amount,
      currency: "RUB",
    },
    confirmation: {
      type: "embedded",
    },
    capture: true,
    description: createYooKassaPaymentInput.description || `Заказ на ${createYooKassaPaymentInput.amount} руб.`,
    metadata: {
      order_id: createYooKassaPaymentInput.orderId,
      profile_id: createYooKassaPaymentInput.profileId,
    },
    merchant_customer_id: createYooKassaPaymentInput.profileId,
  };
};

export const convertAdditionalFeature = (additionalFeature: SupaAdditionalFeature): AdditionalFeature => {
  return {
    additionalFeature: additionalFeature.additional_feature,
    count: additionalFeature.count,
  };
};
